import React, { useContext } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBRadio,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge,
  MDBIcon,
  MDBTooltip
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useConfig } from '../config/ConfigProvider';
import { isValidLectureType, getSpeakerRequirementsForType } from '../validation/lectures.js';
import { DevContext } from '../../../../App';

const getLectureTypeInfo = (t) => ({
  technical: {
    icon: "cogs", // Zahnrad
    title: {
      short: t("Technical Presentation"),
      context: t("spark curiosity through expertise"),
    },
    description: t("Knowledge about guidelines, current research results, project reports, or industry-specific innovations."),
    features: [
      { keyword: t("Expert Knowledge"), tooltip: t("Deep specialized knowledge in the respective field.") },
      { keyword: t("Research"), tooltip: t("Current research results and scientific findings.") },
      { keyword: t("Projects"), tooltip: t("Practical project reports and case studies.") },
      { keyword: t("Guidelines"), tooltip: t("Information about current guidelines and standards.") },
      { keyword: t("Ad-free"), tooltip: t("Content focused on knowledge transfer without promotional elements.") },
    ],
    badges: [
      {
        icons: ["check", "video"],
        color: "primary",
        tooltip: t("This lecture will be recorded and made available."),
        highlighted: false,
      },
      {
        icons: ["globe", "linkedin", "bullhorn"],
        color: "primary",
        tooltip: t("Will be promoted via website, LinkedIn, and other media."),
        highlighted: false,
      },
    ],
  },

  educational: {
    icon: "chalkboard-teacher", // Lehrer mit Tafel
    title: {
      short: t("Training Presentation"),
      context: t("share knowledge"),
    },
    description: t("Presentations specifically designed for knowledge transfer, considering different knowledge levels."),
    features: [
      { keyword: t("Education"), tooltip: t("Focused on structured learning and skill development.") },
      { keyword: t("Competencies"), tooltip: t("Building and enhancing professional competencies.") },
      { keyword: t("Beginners and Experts"), tooltip: t("Content tailored for different experience levels.") },
      { keyword: t("Ad-free"), tooltip: t("Pure educational content without commercial interests.") },
    ],
    badges: [
      {
        icons: ["check", "video"],
        color: "primary",
        tooltip: t("This lecture will be recorded and made available."),
        highlighted: false,
      },
      {
        icons: ["globe", "linkedin", "bullhorn"],
        color: "primary",
        tooltip: t("Will be promoted via website, LinkedIn, and other media."),
        highlighted: false,
      },
    ],
  },

  marketing: {
    icon: "bullhorn", // Lautsprecher
    title: {
      short: t("Marketing Presentation"),
      context: t("reach customers"),
    },
    description: t("Promote products or services."),
    features: [
      { keyword: t("Branding"), tooltip: t("Building and strengthening brand awareness.") },
      { keyword: t("Marketing"), tooltip: t("Strategic promotion of products and services.") },
      { keyword: t("Contacts"), tooltip: t("Building valuable business relationships.") },
      { keyword: t("Leads"), tooltip: t("Generating qualified business leads and opportunities.") },
    ],
    badges: [
      {
        icons: ["ban", "video"],
        color: "danger",
        tooltip: t("This lecture will not be recorded."),
        highlighted: false,
      },
      {
        icons: ["globe"],
        color: "primary",
        tooltip: t("Only listed on the event website."),
        highlighted: false,
      },
    ],
  },

  promotion_technical: {
    icon: "tools", // Werkzeuge für technische Anwendung
    title: {
      short: t("Technical Promotion"),
      context: t("spark curiosity through expertise"),
    },
    description: t("Knowledge about guidelines, current research results, project reports or industry-specific innovations."),
    features: [
      { keyword: t("Expert Knowledge"), tooltip: t("Deep specialized knowledge in the respective field.") },
      { keyword: t("Research"), tooltip: t("Current research results and scientific findings.") },
      { keyword: t("Projects"), tooltip: t("Practical project reports and case studies.") },
      { keyword: t("Guidelines"), tooltip: t("Information about current guidelines and standards.") },
      { keyword: t("Ad-free"), tooltip: t("Content focused on knowledge transfer without promotional elements.") },
    ],
    badges: [
      {
        icons: ["ban", "video"],
        color: "danger",
        tooltip: t("This promotion will not be recorded."),
        highlighted: false,
      },
      {
        icons: ["globe", "calendar"],
        color: "primary",
        tooltip: t("Listed in the official event program."),
        highlighted: false,
      },
    ],
  },

  promotion_marketing: {
    icon: "handshake", // Handshake für Service/Beratung
    title: {
      short: t("Marketing Promotion"),
      context: t("reach customers"),
    },
    description: t("Promote products or services."),
    features: [
      { keyword: t("Branding"), tooltip: t("Building and strengthening brand awareness.") },
      { keyword: t("Marketing"), tooltip: t("Strategic promotion of products and services.") },
      { keyword: t("Contacts"), tooltip: t("Building valuable business relationships.") },
      { keyword: t("Leads"), tooltip: t("Generating qualified business leads and opportunities.") },
    ],
    badges: [
      {
        icons: ["ban", "video"],
        color: "danger",
        tooltip: t("This promotion will not be recorded."),
        highlighted: false,
      },
      {
        icons: ["globe", "calendar"],
        color: "primary",
        tooltip: t("Listed in the official event program."),
        highlighted: false,
      },
    ],
  },

  vip: {
    icon: "crown", // Krone für VIP
    title: {
      short: t("VIP Presentation"),
      context: t("exclusive content, excellently presented"),
    },
    description: t("Special presentations with at least two speakers and strong partners such as authorities or customer projects, exclusively presented on the studio stage or ECV stage."),
    features: [
      { keyword: t("Top Presentation"), tooltip: t("High-quality presentations with exceptional content and speakers.") },
      { keyword: t("Cooperation"), tooltip: t("Joint presentations with strong partners and authorities.") },
      { keyword: t("Exclusive"), tooltip: t("Unique content exclusively available at this event.") },
      { keyword: t("Ad-free"), tooltip: t("Focus on valuable content without commercial promotional elements.") },
    ],
    badges: [
      {
        icons: ["check", "video"],
        color: "primary",
        tooltip: t("This lecture will be recorded and made available."),
        highlighted: false,
      },
      {
        icons: ["globe", "linkedin", "bullhorn"],
        color: "info",
        tooltip: t("Strong promotion via website, LinkedIn, and external media."),
        highlighted: true,
      },
    ],
  },
});

export default function LectureTypeStep({ onComplete, stepId }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, eventConfig } = useValidation();
  const { getStepContent } = useConfig();
  const { isDevMode } = useContext(DevContext);

  // Get content from configuration
  const stepHeading = getStepContent('lecture_type', 'heading');
  const stepDescription = getStepContent('lecture_type', 'description');
  const noTypesMessage = getStepContent('lecture_type', 'noTypes');
  const typeDescriptionPrefix = getStepContent('lecture_type', 'typeDescriptionPrefix');

  const handleTypeChange = (type) => {
    handleFieldChange("type", type);

    // Clear conditions when type changes since they depend on the type
    if (formData.type !== type) {
      handleFieldChange("conditions", []);
    }
  };

  const isTypeSelected = () => {
    return formData.type && formData.type.trim() !== "";
  };

  const isTypeValid = (type) => {
    return isValidLectureType(eventConfig, type);
  };

  const getSpeakerInfo = (type) => {
    if (!type) return { min: 1 };
    return getSpeakerRequirementsForType(eventConfig, type);
  };

  // Get validation info from schema
  const stepValidation = getStepValidation("lecture_type");
  const requiredFields = getStepRequiredFields("lecture_type");

  const handleContinue = () => {
    if (isTypeSelected() && stepValidation.isValid) {
      onComplete?.(stepId);
      goToNextStep();
    }
  };

  const availableTypes = eventConfig?.availableLectureTypes || [];

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {stepHeading || t("Select the category of your lecture")}
      </MDBTypography>

      {availableTypes.length === 0 && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBAlert color="warning">{noTypesMessage || t("No lecture categories available for this event.")}</MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {availableTypes.length > 0 && (
        <MDBRow className="mb-4">
          {availableTypes.map((type) => {
            const typeInfo = getLectureTypeInfo(t)[type];
            const speakerInfo = getSpeakerInfo(type);
            const isSelected = formData.type === type;
            const isValid = isTypeValid(type);

            if (!typeInfo) {
              return (
                <MDBCol key={type} md="6" className="mb-3">
                  <MDBCard className={`h-100 ${isSelected ? "border-primary" : ""}`}>
                    <MDBCardBody>
                      <MDBRadio name="lectureType" id={type} value={type} label={type} checked={isSelected} onChange={(e) => handleTypeChange(e.target.value)} disabled={!isValid} />
                    </MDBCardBody>
                  </MDBCard>
                </MDBCol>
              );
            }

            return (
              <MDBCol key={type} md="12" className="mb-3">
                <MDBCard
                  className={`border-0 shadow-sm px-3 py-4 ${isSelected ? "border border-success shadow-3" : ""}`}
                  style={{ borderRadius: "16px", cursor: isValid ? "pointer" : "not-allowed" }}
                  onClick={() => isValid && handleTypeChange(type)}
                >
                  <MDBCardBody className="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-4">
                    {/* Links: Icon + Text */}
                    <div className="d-flex align-items-start flex-grow-1">
                      <MDBIcon icon={typeInfo.icon} size="2x" className={`me-4`} />

                      <div>
                        <h5 className="fw-bold mb-2">
                          {typeInfo.title.short}
                          <span className="text-muted ms-2 small">– {typeInfo.title.context}</span>
                        </h5>

                        <p className="text-muted small mb-3">{typeInfo.description}</p>

                        {/* Features mit Tooltip */}
                        <div className="d-flex flex-wrap gap-2 text-muted small">
                          {typeInfo.features.map((feature, idx) => (
                            <MDBTooltip tag="span" wrapperClass="badge bg-light border text-dark d-flex align-items-center" title={feature.tooltip} key={idx}>
                              <MDBIcon icon="check" className="me-1" />
                              {feature.keyword}
                            </MDBTooltip>
                          ))}
                        </div>

                        {/* Badges mit Tooltip */}
                        <div className="mt-3 d-flex flex-wrap gap-2">
                          {typeInfo.badges.map((badge, idx) => {
                            const wrapperClasses = ["badge", `bg-${badge.color}`, "d-flex", "align-items-center", "text-white", badge.highlighted ? "highlighted-badge" : ""].join(" ");

                            return (
                              <MDBTooltip key={idx} tag="span" wrapperClass={wrapperClasses} title={badge.tooltip}>
                                {/* Einzelnes Icon */}
                                {badge.icon && <i className={`fas fa-${badge.icon}`}></i>}

                                {/* Mehrere Icons */}
                                {badge.icons &&
                                  badge.icons.map((icon, i) => {
                                    const isBrand = ["linkedin", "facebook", "twitter", "instagram", "github"].includes(icon);
                                    const iconClass = `${isBrand ? "fab" : "fas"} fa-${icon}`;
                                    return <i key={i} className={`${iconClass} ${i < badge.icons.length - 1 ? "me-1" : ""}`}></i>;
                                  })}
                              </MDBTooltip>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Rechts: Radio-Button */}
                    <div className="align-self-center">
                      <MDBRadio
                        name="lectureType"
                        id={type}
                        value={type}
                        checked={isSelected}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        disabled={!isValid}
                        label={t("Auswählen")}
                        labelClass="fw-bold"
                      />
                    </div>
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>
            );
          })}
        </MDBRow>
      )}

      {hasFieldError("type") && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">{getFieldError("type")}</MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Lecture Type Step - Dev Mode)
                </MDBTypography>

                <div className="mb-2">
                  <strong>Available Types:</strong> {availableTypes.join(", ") || "none"}
                </div>

                <div className="mb-2">
                  <strong>Selected Type:</strong> {formData.type || "Not selected"}
                </div>

                <div className="mb-2">
                  <strong>Type Valid:</strong> {formData.type ? (isTypeValid(formData.type) ? "✓" : "✗") : "N/A"}
                </div>

                {formData.type && (
                  <div className="mb-2">
                    <strong>Speaker Requirements:</strong> Min. {getSpeakerInfo(formData.type).min} speaker(s)
                  </div>
                )}

                <div className="mb-2">
                  <strong>Step Required Fields:</strong> {requiredFields.join(", ") || "none"}
                </div>

                <div className="mb-2">
                  <strong>Validation Status:</strong> <MDBBadge color={stepValidation.isValid ? "success" : "danger"}>{stepValidation.isValid ? "Valid" : "Invalid"}</MDBBadge>
                </div>

                {stepValidation.errors.length > 0 && (
                  <div className="mb-2">
                    <strong>Schema Errors:</strong>
                    <ul className="mb-0">
                      {stepValidation.errors.map((error, index) => (
                        <li key={index}>
                          <code>{error.field}</code>: {error.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-2">
                  <strong>Continue Button:</strong>{" "}
                  <MDBBadge color={isTypeSelected() && stepValidation.isValid ? "success" : "warning"}>{isTypeSelected() && stepValidation.isValid ? "Enabled" : "Disabled"}</MDBBadge>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center">
              <MDBBtn onClick={handleContinue} disabled={!isTypeSelected() || !stepValidation.isValid}>
                {t("Continue")}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}
